<script lang="ts" setup>
import {
  BANNER_BRANDING_MAIN_PARAMS,
  BANNER_BRANDING_TEST_PARAMS,
  BANNER_SITE_BOTTOM_FIXED_DESKTOP_PARAMS,
  BANNER_SITE_BOTTOM_FIXED_MOBILE_PARAMS,
  BANNER_SITE_BOTTOM_FIXED_TEST_PARAMS,
} from '~/shared/lib/banners';
import { useYandexMetrikaHead } from '~/shared/utils/useYandexMetrikaHead';
import { useSiteSettings } from '~/shared/lib/useSiteSettings';

const { isPBrandActive } = await useSiteSettings();

const runtimeConfig = useRuntimeConfig();

const isTest = computed(() => runtimeConfig.public.ENVIRONMENT === 'testing');

defineProps<{
  hideBanner?: boolean;
}>();

const bannerLoaded = ref<boolean>(false);

const isArticle = computed(() => {
  if (useError().value) return false;
  const routeName = useRoute().name;
  return routeName
    ? ['news-id', 'news-preview'].includes(routeName as string)
    : false;
});

const isPartnerBlockVisible = useCookie<boolean>('isPartnerBlockVisible', {
  default: () => true,
  watch: true,
  maxAge: undefined,
});

// Partner article: no site-wide ad placements. The flag is set by the
// partner-article.global middleware before render, so it is already correct
// during SSR.
const isPartnerView = useState<boolean>('is-partner-article', () => false);

const hidePartnerBlock = () => {
  isPartnerBlockVisible.value = false;
};

/*    
:placeholder-heights="{
  xs: '768/150',
  mobile: '768/150',
  mobileBig: '768/150',
  tablet: 150,
  tabletBig: '1380/270',
  desktop: '1380/270',
}" 
*/

useYandexMetrikaHead();
</script>

<template>
  <div :class="[{ [$style.root!]: !hideBanner }]">
    <Banner
      v-if="!isPartnerView"
      :config="[
        isTest ? BANNER_BRANDING_TEST_PARAMS : BANNER_BRANDING_MAIN_PARAMS,
        isTest ? BANNER_BRANDING_TEST_PARAMS : BANNER_BRANDING_MAIN_PARAMS,
        isTest ? BANNER_BRANDING_TEST_PARAMS : BANNER_BRANDING_MAIN_PARAMS,
      ]"
      :show-placeholder="isPBrandActive"
      placeholder-variant="transparent"
      :placeholder-heights="{
        xs: '1380/278', // 8px костыль временный
      }"
      :z-index="100"
    />
    <div :class="$style.wrapper">
      <div :class="$style.contentWrapper">
        <!--ArticleBannerSiteTop /-->
        <slot />
      </div>
      <div
        v-if="isPartnerBlockVisible && !isPartnerView"
        :class="[
          $style.wrapper__forma,
          { [$style.wrapper__forma_visible!]: bannerLoaded },
          { [$style.wrapper__forma_article!]: bannerLoaded && isArticle },
        ]"
      >
        <Banner
          :config="[
            isTest
              ? BANNER_SITE_BOTTOM_FIXED_TEST_PARAMS
              : BANNER_SITE_BOTTOM_FIXED_DESKTOP_PARAMS,
            isTest
              ? BANNER_SITE_BOTTOM_FIXED_TEST_PARAMS
              : BANNER_SITE_BOTTOM_FIXED_DESKTOP_PARAMS,
            isTest
              ? BANNER_SITE_BOTTOM_FIXED_TEST_PARAMS
              : BANNER_SITE_BOTTOM_FIXED_MOBILE_PARAMS,
          ]"
          @loaded="bannerLoaded = true"
        />
        <Icon
          name="my-icon:i-close"
          :class="$style.wrapper__formaIcon"
          size="20"
          title="Закрыть"
          @click.prevent="hidePartnerBlock"
        />
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  // font-size: 0;
}

.wrapper {
  margin: 0 auto;
  cursor: initial;

  &__forma {
    position: fixed;
    width: 230px;
    height: 230px;
    bottom: 0;
    right: 0;
    left: -99999px;
    transform: translateX(460px);
    z-index: 2;
    cursor: pointer;
    filter: drop-shadow(-70px 50px 50px rgb(255, 255, 255))
      drop-shadow(0 -10px 30px rgb(255, 255, 255))
      drop-shadow(90px 10px 30px rgb(255, 255, 255));

    &:has(:global(.adfox__banner:empty)) {
      margin-bottom: var(--space-2x);
      background: none;
      padding: 0;
      margin-top: 0;
      display: none;

      .wrapper__formaIcon {
        display: none;
      }
    }

    &_visible {
      display: block;
      left: 50%;
    }

    &_article {
      transform: translateX(150px);
    }

    &Icon {
      position: absolute;
      right: 4px;
      top: -28px;
      background-color: rgba(255, 255, 255, 0.8);
      border-radius: 50%;
      cursor: pointer;
      width: 24px;
      height: 24px;
      z-index: 2;
      path {
        fill: var(--color-gray-text);
      }
    }
  }
}

.contentWrapper {
  position: relative;
  background: #fff;
}

@media (max-width: 1379px) {
  .wrapper {
    // for FORMA BEGIN

    &__forma {
      transform: translateX(282px);

      &_article {
        transform: translateX(26px);
      }
    }

    // for FORMA END
  }
}

@media (max-width: 1023px) {
  .wrapper {
    &__forma {
      transform: translateX(154px);
    }
  }
}

@media (max-width: 767px) {
  .wrapper {
    &__forma {
      transform: translateX(70px);
    }
  }
}
@media (max-width: 599px) {
  .wrapper {
    &__forma {
      width: 150px;
      height: 150px;
      background-size: cover;
      transform: none;
      right: 0;
      left: inherit;
    }
  }
}

@media (min-width: 1380px) {
  .wrapper {
    width: 1380px;
  }
}

@media (min-width: 1024px) and (max-width: 1379px) {
  .wrapper {
    width: 1024px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .wrapper {
    width: 768px;
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .wrapper {
    width: 600px;
  }
}

@media (max-width: 599px) {
  .wrapper {
    width: 100%;
  }
}
</style>
